import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '../../common/Modal';
import ComplaintDetailsTab from './ComplaintDetailsTab';
import AssignmentActionsTab from './AssignmentActionsTab';
import ResolutionDetailsTab from './ResolutionDetailsTab';
import { useSubmission, useFetch } from '../../../hooks/useCallApi';
import { complaintsAPI } from '../../../services/api/admin/complaintsApi';
import toast from 'react-hot-toast';

const ComplaintFormModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  selectedComplaint,
  onComplaintCreated,
  onComplaintUpdated 
}) => {
  const isEditMode = !!selectedComplaint;

  const [isAnonymous, setIsAnonymous] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [resolutionFiles, setResolutionFiles] = useState([]);
  const [activeTab, setActiveTab] = useState('complainant');
  const [categories, setCategories] = useState([]);
  const [complaintData, setComplaintData] = useState(null);
  const [filesToRemove, setFilesToRemove] = useState([]);
  const [feedbackFilesToRemove, setFeedbackFilesToRemove] = useState([]);

  const [searchTerms, setSearchTerms] = useState({
    resident: '',
    assignedTo: '',
    assignedBy: '',
    actionBy: '',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    control,
    getValues,
  } = useForm({
    shouldUnregister: false,
    defaultValues: {
      resident_id: null,
      complainant_purok: '',
      description: '',
      category_id: '',
      priority: '',
      status: '',
      landmark: '',
      assignment: {
        assigned_to: '',
        assigned_by: '',
        remarks: '',
      },
      action: {
        action_by: '',
        action_date: '',
        action_note: '',
      },
      feedback: {
        rating: '',
        comment: '',
      },
    },
  });

  const { isLoading: isLoadingComplaint, refetch: refetchComplaint } = useFetch(
    ['complaint', selectedComplaint?.id],
    (api) => complaintsAPI.getById(selectedComplaint.id),
    {
      enabled: isEditMode && isOpen && !!selectedComplaint?.id,
      onSuccess: (response) => {
        console.log("✅ Fetched complaint data:", response);
        const data = response?.data || response;
        console.log("COMPLAINTS DATA IN FORM: ", data)
        setComplaintData(data);
        
        setIsAnonymous(data.is_anonymous === 1);
        
        populateFormWithData(data);
      },
      onError: (error) => {
        console.error("❌ Error fetching complaint:", error);
        toast.error("Failed to load complaint data");
      }
    }
  );

  const createMutation = useSubmission(
    ['create-complaint'],
    (api, payload) => complaintsAPI.create(payload),
    {
      onSuccess: (response) => {
        toast.success('Complaint created successfully');
        resetForm();
        
        if (onSuccess) {
          onSuccess(response);
        }
        if (onComplaintCreated) {
          onComplaintCreated();
        }
        
        onClose();
      },
      onError: (error) => {
        console.error('Create error:', error);
        toast.error(error?.response?.data?.message || 'Failed to create complaint');
      }
    }
  );

  const updateMutation = useSubmission(
    ['update-complaint'],
    (api, payload) => complaintsAPI.update(selectedComplaint.id, payload),
    {
      onSuccess: (response) => {
        toast.success('Complaint updated successfully');
        resetForm();
        
        if (onSuccess) {
          onSuccess(response);
        }
        if (onComplaintUpdated) {
          onComplaintUpdated();
        }
        
        onClose();
      },
      onError: (error) => {
        console.error('Update error:', error);
        toast.error(error?.response?.data?.message || 'Failed to update complaint');
      }
    }
  );

  const resetForm = () => {
    reset({
      resident_id: null,
      complainant_purok: '',
      description: '',
      category_id: '',
      priority: '',
      status: '',
      landmark: '',
      assignment: {
        assigned_to: '',
        assigned_by: '',
        remarks: '',
      },
      action: {
        action_by: '',
        action_date: '',
        action_note: '',
      },
      feedback: {
        rating: '',
        comment: '',
      },
    });
    
    setIsAnonymous(false);
    setUploadedFiles([]);
    setResolutionFiles([]);
    setComplaintData(null);
    setFilesToRemove([]);
    setFeedbackFilesToRemove([]);
    setSearchTerms({
      resident: '',
      assignedTo: '',
      assignedBy: '',
      actionBy: '',
    });
    setActiveTab('complainant');
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      resetForm();
    }
  }, [isOpen, isEditMode]);

  const populateFormWithData = (data) => {
    console.log('🔧 Populating form with data:', data);
    
    if (data.resident) {
      setSearchTerms(prev => ({
        ...prev,
        resident: data.resident.name || ''
      }));
    }

    const categoryId = data.category?.category_id || '';

    let actionDate = '';
    if (data.action?.action_date) {
      if (data.action.action_date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        actionDate = data.action.action_date;
      } else {
        try {
          const date = new Date(data.action.action_date);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            actionDate = `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error('Error parsing date:', e);
        }
      }
    }

    console.log('🔧 Setting category_id to:', categoryId);
    console.log('🔧 Setting purok to:', data.purok);

    reset({
      resident_id: data.resident?.resident_id || null,
      complainant_purok: data.purok || '',
      description: data.description || '',
      category_id: categoryId,
      priority: data.priority || '',
      status: data.status || '',
      landmark: data.landmark || '',
      assignment: {
        assigned_to: data.assignment?.assigned_to?.user_id?.toString() || '',
        assigned_by: data.assignment?.assigned_by?.user_id?.toString() || '',
        remarks: data.assignment?.remarks || '',
      },
      action: {
        action_by: data.action?.action_by?.user_id?.toString() || '',
        action_date: actionDate,
        action_note: data.action?.action_note || '',
      },
      feedback: {
        rating: data.feedback?.rating || '',
        comment: data.feedback?.comment || '',
      },
    });

    if (data.assignment?.assigned_to?.name) {
      setSearchTerms(prev => ({
        ...prev,
        assignedTo: data.assignment.assigned_to.name
      }));
    }
    if (data.assignment?.assigned_by?.name) {
      setSearchTerms(prev => ({
        ...prev,
        assignedBy: data.assignment.assigned_by.name
      }));
    }
    if (data.action?.action_by?.name) {
      setSearchTerms(prev => ({
        ...prev,
        actionBy: data.action.action_by.name
      }));
    }

    if (data.feedback?.file_url && data.feedback.file_url.length > 0) {
      console.log("📄 Loading existing resolution files:", data.feedback.file_url);
      const existingFiles = data.feedback.file_url.map((url, index) => ({
        id: `existing-${Date.now()}-${index}`,
        url: url,
        name: url.split('/').pop() || `Document ${index + 1}`,
        size: 0,
        type: 'application/octet-stream',
        progress: 100,
        status: 'completed',
        isExisting: true,
        markedForRemoval: false
      }));
      
      setResolutionFiles(existingFiles);
    }

    console.log('✅ Form populated with action_date:', actionDate);
  };

  useEffect(() => {
    if (isEditMode && isOpen && selectedComplaint?.id) {
      refetchComplaint();
    }
  }, [isEditMode, isOpen, selectedComplaint?.id, refetchComplaint]);

  const buildPayload = (data) => {
    const hasFiles = uploadedFiles.length > 0 || 
                     resolutionFiles.some(f => !f.isExisting) ||
                     filesToRemove.length > 0 || 
                     feedbackFilesToRemove.length > 0;

    if (hasFiles) {
      const formData = new FormData();
      
      if (isEditMode) {
        formData.append('_method', 'POST'); 
      }

      formData.append('is_anonymous', isAnonymous ? '1' : '0');
      formData.append('purok', isAnonymous ? 'N/A' : data.complainant_purok || '');
      formData.append('description', data.description || '');
      
      if (data.category_id) {
        formData.append('category_id', parseInt(data.category_id, 10));
      }
      
      if (!isAnonymous && data.resident_id) {
        formData.append('resident_id', parseInt(data.resident_id, 10));
      }
      
      if (data.status && data.status.trim() !== '') {
        formData.append('status', data.status);
      }
      
      if (data.priority && data.priority.trim() !== '') {
        formData.append('priority', data.priority);
      }
      
      if (data.landmark && data.landmark.trim() !== '') {
        formData.append('landmark', data.landmark);
      }

      if (data.assignment) {
        if (data.assignment.assigned_to && data.assignment.assigned_to.toString().trim() !== '') {
          formData.append('assignment[assigned_to]', parseInt(data.assignment.assigned_to, 10));
        }
        if (data.assignment.assigned_by && data.assignment.assigned_by.toString().trim() !== '') {
          formData.append('assignment[assigned_by]', parseInt(data.assignment.assigned_by, 10));
        }
        if (data.assignment.remarks && data.assignment.remarks.trim() !== '') {
          formData.append('assignment[remarks]', data.assignment.remarks);
        }
      }

      if (data.action) {
        if (data.action.action_by && data.action.action_by.toString().trim() !== '') {
          formData.append('action[action_by]', parseInt(data.action.action_by, 10));
        }
        if (data.action.action_date && data.action.action_date.trim() !== '') {
          formData.append('action[action_date]', data.action.action_date);
        }
        if (data.action.action_note && data.action.action_note.trim() !== '') {
          formData.append('action[action_note]', data.action.action_note);
        }
      }

      if (data.feedback?.rating && data.feedback.rating.toString().trim() !== '') {
        formData.append('feedback[rating]', parseInt(data.feedback.rating, 10));
      }
      
      if (data.feedback?.comment && data.feedback.comment.trim() !== '') {
        formData.append('feedback[comment]', data.feedback.comment);
      }

      uploadedFiles.forEach((fileObj) => {
        if (!fileObj.isExisting && fileObj.file) {
          formData.append('supporting_documents[]', fileObj.file);
        }
      });

      if (filesToRemove.length > 0) {
        filesToRemove.forEach(fileId => {
          formData.append('remove_attachment_ids[]', fileId);
        });
      }

      resolutionFiles.forEach((fileObj) => {
        if (!fileObj.isExisting && fileObj.file) {
          formData.append('feedback[documents][]', fileObj.file);
        }
      });

      if (feedbackFilesToRemove.length > 0) {
        feedbackFilesToRemove.forEach(url => {
          formData.append('feedback[remove_document_urls][]', url);
        });
      }

      console.log('📦 FormData created with feedback fields');
      return formData;
    } 
    
    else {
      const payload = {
        is_anonymous: isAnonymous ? 1 : 0,
        purok: isAnonymous ? 'N/A' : data.complainant_purok,
        description: data.description,
        category_id: data.category_id ? parseInt(data.category_id, 10) : null,
      };

      if (!isAnonymous && data.resident_id) {
        payload.resident_id = parseInt(data.resident_id, 10);
      }

      if (data.status && data.status.trim() !== '') {
        payload.status = data.status;
      }
      if (data.priority && data.priority.trim() !== '') {
        payload.priority = data.priority;
      }
      if (data.landmark && data.landmark.trim() !== '') {
        payload.landmark = data.landmark;
      }

      if (data.assignment) {
        const hasAssignmentFields =
          (data.assignment.assigned_to && data.assignment.assigned_to.toString().trim() !== '') ||
          (data.assignment.assigned_by && data.assignment.assigned_by.toString().trim() !== '') ||
          (data.assignment.remarks && data.assignment.remarks.trim() !== '');

        if (hasAssignmentFields) {
          payload.assignment = {};

          if (data.assignment.assigned_to && data.assignment.assigned_to.toString().trim() !== '') {
            payload.assignment.assigned_to = parseInt(data.assignment.assigned_to, 10);
          }
          if (data.assignment.assigned_by && data.assignment.assigned_by.toString().trim() !== '') {
            payload.assignment.assigned_by = parseInt(data.assignment.assigned_by, 10);
          }
          if (data.assignment.remarks && data.assignment.remarks.trim() !== '') {
            payload.assignment.remarks = data.assignment.remarks;
          }
        }
      }

      if (data.action) {
        const hasActionFields =
          (data.action.action_by && data.action.action_by.toString().trim() !== '') ||
          (data.action.action_date && data.action.action_date.trim() !== '') ||
          (data.action.action_note && data.action.action_note.trim() !== '');

        if (hasActionFields) {
          payload.action = {};

          if (data.action.action_by && data.action.action_by.toString().trim() !== '') {
            payload.action.action_by = parseInt(data.action.action_by, 10);
          }
          if (data.action.action_date && data.action.action_date.trim() !== '') {
            payload.action.action_date = data.action.action_date;
          }
          if (data.action.action_note && data.action.action_note.trim() !== '') {
            payload.action.action_note = data.action.action_note;
          }
        }
      }

      const hasFeedbackFields =
        (data.feedback?.rating && data.feedback.rating.toString().trim() !== '') ||
        (data.feedback?.comment && data.feedback.comment.trim() !== '');

      if (hasFeedbackFields) {
        payload.feedback = {};

        if (data.feedback?.rating && data.feedback.rating.toString().trim() !== '') {
          payload.feedback.rating = parseInt(data.feedback.rating, 10);
        }

        if (data.feedback?.comment && data.feedback.comment.trim() !== '') {
          payload.feedback.comment = data.feedback.comment;
        }
      }

      console.log("📦 JSON Payload:", payload);
      return payload;
    }
  };

  const handleFormSubmit = (data) => {
    const payload = buildPayload(data);
    console.log('🚀 Submitting payload:', payload);

    if (isEditMode) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const TabNavigation = () => (
    <div className="flex border-b border-gray-200 mb-6">
      {['complainant', 'assignment', 'resolution'].map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => setActiveTab(tab)}
          className={`flex-1 py-2 text-sm font-medium border-b-2 ${
            activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'
          }`}
        >
          {tab === 'complainant' && 'Complaint Details'}
          {tab === 'assignment' && 'Assignment & Actions'}
          {tab === 'resolution' && 'Resolution Details'}
        </button>
      ))}
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" alignment="top" title={isEditMode ? 'Edit Complaint' : 'Create Complaint'}>
      {isEditMode && isLoadingComplaint ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-center">
            <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">Loading complaint data...</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 max-h-[70vh] overflow-y-auto px-3">
          <TabNavigation />

          <div className={activeTab === 'complainant' ? 'block' : 'hidden'}>
            <ComplaintDetailsTab
              control={control}
              register={register}
              errors={errors}
              isAnonymous={isAnonymous}
              setIsAnonymous={setIsAnonymous}
              setValue={setValue}
              watch={watch}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              searchTerm={searchTerms.resident}
              setSearchTerm={(term) => setSearchTerms((p) => ({ ...p, resident: term }))}
              categories={categories}
              setCategories={setCategories}
              complaintData={complaintData}
              setFilesToRemove={setFilesToRemove}
            />
          </div>

          <div className={activeTab === 'assignment' ? 'block' : 'hidden'}>
            <AssignmentActionsTab
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              searchTerms={searchTerms}
              setSearchTerms={setSearchTerms}
              complaintData={complaintData}
            />
          </div>

          <div className={activeTab === 'resolution' ? 'block' : 'hidden'}>
            <ResolutionDetailsTab
              register={register}
              watch={watch}
              errors={errors}
              resolutionFiles={resolutionFiles}
              setResolutionFiles={setResolutionFiles}
              complaintData={complaintData}
              setFilesToRemove={setFeedbackFilesToRemove}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading || isLoadingComplaint}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(createMutation.isLoading || updateMutation.isLoading)
                ? 'Saving...'
                : isEditMode
                  ? 'Update'
                  : 'Create'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ComplaintFormModal;